import React, { useState } from 'react';
import { useSimulations, useDeleteSimulation } from '../../hooks/useSimulations';
import { SimulationRunner } from './SimulationRunner';
import { SimulationList } from './SimulationList';
import { SimulationViewer } from './SimulationViewer';
import type { Simulation } from '../../types';

interface SimulationsTabProps {
  projectId: string;
}

export const SimulationsTab: React.FC<SimulationsTabProps> = ({ projectId }) => {
  const { simulations, loading } = useSimulations(projectId);
  const { deleteSimulation } = useDeleteSimulation();

  const [showRunner, setShowRunner] = useState(false);
  const [selectedSimulation, setSelectedSimulation] = useState<Simulation | null>(null);

  const handleComplete = () => {
    setShowRunner(false);
  };

  const handleDelete = async (simulationId: string) => {
    try {
      await deleteSimulation(simulationId);
    } catch (err) {
      console.error('Failed to delete simulation:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Simulations</h2>
          <p className="mt-1 text-sm text-gray-500">
            Run focus groups and surveys with your personas
          </p>
        </div>
        <button
          onClick={() => setShowRunner(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-500 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <svg
            className="w-5 h-5 mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          New Simulation
        </button>
      </div>

      {/* Simulation List */}
      <SimulationList
        simulations={simulations}
        onSelect={setSelectedSimulation}
        onDelete={handleDelete}
      />

      {/* Modals */}
      {showRunner && (
        <SimulationRunner
          projectId={projectId}
          onComplete={handleComplete}
          onCancel={() => setShowRunner(false)}
        />
      )}

      {selectedSimulation && (
        <SimulationViewer
          simulation={selectedSimulation}
          onClose={() => setSelectedSimulation(null)}
        />
      )}
    </div>
  );
};
